import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { vendorProfileAPI, categoryAPI, locationAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const S = {
  page: { minHeight: '100vh', background: 'var(--cream)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 },
  card: { background: '#fff', borderRadius: 16, padding: 36, width: '100%', maxWidth: 560, border: '1px solid var(--border)', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' },
  logo: { fontFamily: 'var(--font-serif)', fontSize: 24, fontWeight: 900, color: '#1B5C3A', marginBottom: 4 },
  dot: { color: '#F5A623', fontSize: 28, fontWeight: 900 },
  title: { fontFamily: 'var(--font-serif)', fontSize: 22, fontWeight: 700, color: 'var(--char)', marginBottom: 4, marginTop: 20 },
  subtitle: { fontSize: 13, color: 'var(--muted)', marginBottom: 24 },
  label: { display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--char)', marginBottom: 4 },
  input: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, boxSizing: 'border-box', marginBottom: 14 },
  select: { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border)', fontSize: 14, background: '#fff', marginBottom: 14 },
  formRow: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 },
  catGrid: { display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14 },
  catChip: (selected) => ({
    padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: 'pointer',
    border: selected ? '2px solid #1B5C3A' : '1px solid var(--border)',
    background: selected ? '#E8F5EE' : '#fff', color: selected ? '#1B5C3A' : 'var(--char)',
  }),
  toggleRow: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 },
  toggle: (active) => ({
    width: 44, height: 24, borderRadius: 12, cursor: 'pointer', border: 'none',
    background: active ? '#1B5C3A' : '#ccc', position: 'relative', transition: 'background 0.2s',
  }),
  toggleDot: (active) => ({
    width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 3,
    left: active ? 23 : 3, transition: 'left 0.2s',
  }),
  error: { color: '#DC2626', fontSize: 12, marginBottom: 10 },
};

export default function VendorOnboarding() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [catTree, setCatTree] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Prefill firm / GSTIN / state / city from the /vendor-signup hand-off so
  // the vendor doesn't retype what they already entered on the marketing form.
  const [form, setForm] = useState({
    firmName: searchParams.get('firmName') || '',
    gstin:    searchParams.get('gstin')    || '',
    city:     searchParams.get('city')     || '',
    state:    searchParams.get('state')    || '',
    categories: [], gstRegistered: false,
  });

  useEffect(() => {
    // Load categories + master state list in parallel on mount.
    Promise.all([
      categoryAPI.tree().then(r => setCatTree(r.data.data || [])).catch(() => {}),
      locationAPI.states().then(r => setStates(r.data.data || [])).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  // Cascade: cities depend on the selected state. Clear city if state changes.
  useEffect(() => {
    if (!form.state) { setCities([]); return; }
    locationAPI.cities(form.state).then(r => setCities(r.data.data || [])).catch(() => setCities([]));
  }, [form.state]);

  const handleChange = (field, val) => setForm({ ...form, [field]: val });

  const toggleCategory = (catId) => {
    const cats = form.categories.includes(catId)
      ? form.categories.filter(c => c !== catId)
      : [...form.categories, catId];
    setForm({ ...form, categories: cats });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.firmName || !form.city || !form.state || form.categories.length === 0) {
      setError('Please fill in firm name, city, state, and select at least one category');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      await vendorProfileAPI.create({
        firmName: form.firmName,
        gstin: form.gstin || undefined,
        city: form.city,
        state: form.state,
        categories: form.categories,
        gstRegistered: form.gstRegistered,
      });
      navigate('/vendor/dashboard');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create vendor profile');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={S.page}>
      <div style={S.card}>
        <div>
          <span style={S.logo}>Kanjusee</span><span style={S.dot}>.</span>
        </div>
        <div style={S.title}>Vendor Setup</div>
        <div style={S.subtitle}>Complete your profile to start receiving leads</div>

        <form onSubmit={handleSubmit}>
          {error && <div style={S.error}>{error}</div>}

          <label style={S.label}>Firm Name *</label>
          <input style={S.input} className="form-input" placeholder="Your business name" value={form.firmName} onChange={e => handleChange('firmName', e.target.value)} />

          {/* GST Toggle */}
          <div style={S.toggleRow}>
            <button type="button" style={S.toggle(form.gstRegistered)} onClick={() => handleChange('gstRegistered', !form.gstRegistered)}>
              <div style={S.toggleDot(form.gstRegistered)} />
            </button>
            <span style={{ fontSize: 13, color: 'var(--char)', fontWeight: 600 }}>GST Registered</span>
          </div>

          {form.gstRegistered && (
            <>
              <label style={S.label}>GSTIN</label>
              <input style={S.input} className="form-input" placeholder="22AAAAA0000A1Z5" value={form.gstin}
                onChange={e => handleChange('gstin', e.target.value.toUpperCase())} maxLength={15} />
            </>
          )}

          <div style={S.formRow}>
            <div>
              <label style={S.label}>State *</label>
              <select
                style={S.select}
                value={form.state}
                onChange={e => setForm(f => ({ ...f, state: e.target.value, city: '' }))}
              >
                <option value="">Select state</option>
                {states.map(s => <option key={s.state || s} value={s.state || s}>{s.state || s}</option>)}
              </select>
            </div>
            <div>
              <label style={S.label}>City *</label>
              <select
                style={{ ...S.select, cursor: form.state ? 'pointer' : 'not-allowed' }}
                value={form.city}
                onChange={e => handleChange('city', e.target.value)}
                disabled={!form.state}
              >
                <option value="">{form.state ? 'Select city' : 'Pick a state first'}</option>
                {cities.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          <label style={S.label}>Categories * (select all that apply)</label>
          {loading ? (
            <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14 }}>Loading categories...</div>
          ) : (
            <div style={S.catGrid}>
              {catTree.map(c => (
                <button type="button" key={c._id} style={S.catChip(form.categories.includes(c._id))} onClick={() => toggleCategory(c._id)}>
                  {c.name}
                </button>
              ))}
              {catTree.length === 0 && (
                <div style={{ fontSize: 12, color: 'var(--muted)' }}>No categories available</div>
              )}
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', padding: '12px 0', borderRadius: 10, border: 'none',
              background: '#1B5C3A', color: '#fff', fontSize: 15, fontWeight: 700,
              cursor: 'pointer', opacity: submitting ? 0.6 : 1, marginTop: 8,
            }}>
            {submitting ? 'Setting up...' : 'Complete Setup & Go to Dashboard'}
          </button>
        </form>
      </div>
    </div>
  );
}
